'use client';

import { channelCategoriesQueryOptions } from '@/api/options';
import {
  IChannelCategoryV2Response,
  IHomeFollowingChannelResponse,
} from '@/api/types';
import { useChannelSearchModal } from '@/app/(home)/components/channelSearch';
import {
  getRouterPath,
  HorizontalScrollbar,
  NavigationBarControls,
  ProfileImage,
  VuddyShortLogoLink,
} from '@/components';
import { VuddyLogoLink } from '@/components/vuddyLogoLink/VuddyLogoLink';
import {
  useHomeFollowingChannelsQuery,
  useHomeNoticesQuery,
  useIsLoggedIn,
} from '@/entities';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { qaAttr, qaId } from '@/lib/qa';
import { useQuery } from '@/lib/useWrappingQuery';
import { Element, Icon } from '@vuddy/components';
import { trackEvent } from '@vuddy/utils';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HomeHlsVideo } from './HomeHlsVideo';
import * as styles from './vuddyHome.css';

const AUTO_ROLLING_MS = 3000;
const DESKTOP_VISIBLE_RADIUS = 4;
const TABLET_VISIBLE_RADIUS = 3;
const MOBILE_VISIBLE_RADIUS = 2;
const DESKTOP_CARD_SPACING = 366;
const TABLET_CARD_SPACING = 322;
const MOBILE_CARD_SPACING = 284;
const TAP_MOVE_THRESHOLD = 8;
const CARD_TRANSITION_MS = 420;
const CARD_TRANSITION =
  'transform 420ms cubic-bezier(0.25, 1, 0.3, 1), opacity 300ms ease';
const WHEEL_MIN_DELTA = 1;
const WHEEL_INDEX_SPEED = 0.004;
const WHEEL_SNAP_IDLE_MS = 260;
const NOTICE_MARQUEE_GROUP_COUNT = 2;
const NOTICE_TEXT_REPEAT_COUNT = 4;

interface HomeCard {
  key: string;
  category: IChannelCategoryV2Response;
  videoUrl?: string;
  posterUrl?: string;
  backgroundUrl?: string;
  mobileBackgroundUrl?: string;
}

interface RenderedHomeCard {
  renderKey: string;
  card: HomeCard;
  cardIndex: number;
  virtualIndex: number;
  offset: number;
}

const getMediaUrl = (
  ...mediaFiles: Array<{ url?: string } | null | undefined>
) => mediaFiles.map(mediaFile => mediaFile?.url).find(Boolean);

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getCardElevationShadow = (progress: number) => {
  if (progress <= 0) return '0 4px 10px rgba(0, 0, 0, 0.07)';

  const primaryY = 4 + 6 * progress;
  const primaryBlur = 10 + 5 * progress;
  const primaryAlpha = 0.07 + 0.03 * progress;
  const secondaryY = 4 * progress;
  const secondaryBlur = 6 * progress;
  const secondaryAlpha = 0.05 * progress;

  return [
    `0 ${primaryY}px ${primaryBlur}px rgba(0, 0, 0, ${primaryAlpha})`,
    `0 ${secondaryY}px ${secondaryBlur}px rgba(0, 0, 0, ${secondaryAlpha})`,
  ].join(', ');
};

const getCardScale = (absOffset: number) => {
  if (absOffset <= 1) return 0.78 + (1 - absOffset) * 0.22;

  return clamp(0.78 - (absOffset - 1) * 0.14, 0.44, 0.78);
};

const getWrappedIndex = (index: number, length: number) =>
  ((index % length) + length) % length;

const getShortestCircularOffset = (
  cardIndex: number,
  carouselIndex: number,
  length: number
) => {
  if (length <= 0) return 0;

  const offset = cardIndex - carouselIndex;
  const halfLength = length / 2;

  return ((((offset + halfLength) % length) + length) % length) - halfLength;
};

const getNormalizedWheelDelta = (event: WheelEvent) => {
  const delta =
    Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;

  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return delta * 16;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return delta * 120;

  return delta;
};

const getCardHref = (category: IChannelCategoryV2Response) => {
  const routerPath = getRouterPath();

  if (category.type === 'ETC') {
    return category.landingUrl || undefined;
  }

  return routerPath.categoryHome(category.id);
};

const toHomeCards = (categories: IChannelCategoryV2Response[] | undefined) => {
  const source = categories ?? [];
  return source
    .filter(category => category.videoPreview?.url || category.video?.url)
    .map<HomeCard>(category => {
      const backgroundUrl = getMediaUrl(category.videoPreview);

      return {
        key: `${category.type}-${category.id}`,
        category,
        videoUrl: category.video?.url,
        posterUrl: category.videoPreview?.url,
        backgroundUrl,
        mobileBackgroundUrl: backgroundUrl,
      };
    });
};

const getVisibleRadius = (device: ReturnType<typeof useDeviceSize>) => {
  if (device === 'mobile') return MOBILE_VISIBLE_RADIUS;
  if (device === 'tablet') return TABLET_VISIBLE_RADIUS;
  if (device === 'desktop') return DESKTOP_VISIBLE_RADIUS;

  return MOBILE_VISIBLE_RADIUS;
};

const getCardSpacing = (device: ReturnType<typeof useDeviceSize>) => {
  if (device === 'desktop') return DESKTOP_CARD_SPACING;
  if (device === 'tablet') return TABLET_CARD_SPACING;

  return MOBILE_CARD_SPACING;
};

const getRenderedCards = (
  cards: HomeCard[],
  carouselIndex: number
): RenderedHomeCard[] => {
  if (cards.length === 0) return [];

  return cards.map((card, cardIndex) => {
    const offset = getShortestCircularOffset(
      cardIndex,
      carouselIndex,
      cards.length
    );
    const virtualIndex = carouselIndex + offset;

    return {
      renderKey: card.key,
      card,
      cardIndex,
      virtualIndex,
      offset,
    };
  });
};

export const VuddyHome = () => {
  const router = useRouter();
  const device = useDeviceSize();
  const { open: openChannelSearch } = useChannelSearchModal();
  const { isLoggedIn } = useIsLoggedIn();
  const { data: categories, isError } = useQuery({
    ...channelCategoriesQueryOptions.getChannelCategoriesChannelCategoryV2(),
    retry: false,
    skipDefaultErrorHandling: true,
    staleTime: AUTO_ROLLING_MS * 10,
  });
  const { data: notices } = useHomeNoticesQuery();
  const followingChannelsQuery = useHomeFollowingChannelsQuery();
  const followingChannels = followingChannelsQuery.data ?? [];
  const cards = useMemo(() => toHomeCards(categories), [categories]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const renderedCards = useMemo(
    () => getRenderedCards(cards, carouselIndex),
    [carouselIndex, cards]
  );
  const [isDragging, setIsDragging] = useState(false);
  const pointerStartXRef = useRef<number | null>(null);
  const pointerStartIndexRef = useRef(0);
  const pointerMovedRef = useRef(false);
  const suppressNextClickRef = useRef(false);
  const autoRollingTimerRef = useRef<number | null>(null);
  const autoRollingResumeTimerRef = useRef<number | null>(null);
  const wheelSnapTimerRef = useRef<number | null>(null);
  const transitionLockTimerRef = useRef<number | null>(null);
  const cardStageRef = useRef<HTMLElement | null>(null);
  const [isTransitionLocked, setIsTransitionLocked] = useState(false);
  const visibleRadius = getVisibleRadius(device);
  const cardSpacing = getCardSpacing(device);
  const activeIndex =
    cards.length > 0
      ? getWrappedIndex(Math.round(carouselIndex), cards.length)
      : 0;
  const activeCard = cards.length > 0 ? cards[activeIndex] : null;
  const activeBackgroundUrl =
    device === 'mobile'
      ? activeCard?.mobileBackgroundUrl
      : activeCard?.backgroundUrl;
  const notice = notices?.[0];

  useEffect(() => {
    if (cards.length === 0) {
      setCarouselIndex(0);
      return;
    }

    setCarouselIndex(index => Math.min(index, cards.length - 1));
  }, [cards.length]);

  const clearAutoRollingTimer = () => {
    if (autoRollingTimerRef.current) {
      window.clearTimeout(autoRollingTimerRef.current);
      autoRollingTimerRef.current = null;
    }
  };

  const clearAutoRollingResumeTimer = () => {
    if (autoRollingResumeTimerRef.current) {
      window.clearTimeout(autoRollingResumeTimerRef.current);
      autoRollingResumeTimerRef.current = null;
    }
  };

  const clearTransitionLockTimer = () => {
    if (transitionLockTimerRef.current) {
      window.clearTimeout(transitionLockTimerRef.current);
      transitionLockTimerRef.current = null;
    }
  };

  const clearTransitionLock = () => {
    clearTransitionLockTimer();
    setIsTransitionLocked(false);
  };

  const lockActiveCardDuringTransition = () => {
    clearTransitionLockTimer();
    setIsTransitionLocked(true);

    transitionLockTimerRef.current = window.setTimeout(() => {
      transitionLockTimerRef.current = null;
      setIsTransitionLocked(false);
    }, CARD_TRANSITION_MS);
  };

  const startAutoRolling = () => {
    clearAutoRollingTimer();

    if (cards.length <= 1) return;

    autoRollingTimerRef.current = window.setTimeout(() => {
      lockActiveCardDuringTransition();
      setCarouselIndex(index => index + 1);
      startAutoRolling();
    }, AUTO_ROLLING_MS);
  };

  const pauseAutoRolling = () => {
    clearAutoRollingTimer();
    clearAutoRollingResumeTimer();
  };

  const resumeAutoRollingAfterDelay = () => {
    pauseAutoRolling();

    if (cards.length <= 1) return;

    autoRollingResumeTimerRef.current = window.setTimeout(() => {
      autoRollingResumeTimerRef.current = null;
      startAutoRolling();
    }, AUTO_ROLLING_MS);
  };

  useEffect(() => {
    startAutoRolling();

    return () => {
      clearAutoRollingTimer();
      clearAutoRollingResumeTimer();
    };
  }, [cards.length]);

  useEffect(() => {
    return () => {
      clearAutoRollingTimer();
      clearAutoRollingResumeTimer();
      if (wheelSnapTimerRef.current) {
        window.clearTimeout(wheelSnapTimerRef.current);
      }
      clearTransitionLockTimer();
    };
  }, []);

  const finishPointerInteraction = (clientX: number) => {
    const startX = pointerStartXRef.current;
    pointerStartXRef.current = null;
    pointerMovedRef.current = false;
    setIsDragging(false);

    if (startX === null) return;

    const deltaX = clientX - startX;
    if (Math.abs(deltaX) >= TAP_MOVE_THRESHOLD) {
      suppressNextClickRef.current = true;
    }

    lockActiveCardDuringTransition();
    setCarouselIndex(index => Math.round(index));
    resumeAutoRollingAfterDelay();
  };

  const resetPointerInteraction = () => {
    if (pointerStartXRef.current === null) return;

    pointerStartXRef.current = null;
    pointerMovedRef.current = false;
    setIsDragging(false);
    lockActiveCardDuringTransition();
    setCarouselIndex(index => Math.round(index));
    resumeAutoRollingAfterDelay();
  };

  useEffect(() => {
    const handleWindowPointerUp = (event: globalThis.PointerEvent) => {
      finishPointerInteraction(event.clientX);
    };

    window.addEventListener('pointerup', handleWindowPointerUp);
    window.addEventListener('pointercancel', resetPointerInteraction);

    return () => {
      window.removeEventListener('pointerup', handleWindowPointerUp);
      window.removeEventListener('pointercancel', resetPointerInteraction);
    };
  });

  useEffect(() => {
    const stage = cardStageRef.current;
    if (!stage) return;

    const handleNativeWheel = (event: WheelEvent) => {
      const delta = getNormalizedWheelDelta(event);

      if (Math.abs(delta) < WHEEL_MIN_DELTA) return;

      event.preventDefault();
      pauseAutoRolling();
      clearTransitionLock();
      setIsDragging(true);
      setCarouselIndex(index => index + delta * WHEEL_INDEX_SPEED);

      if (wheelSnapTimerRef.current) {
        window.clearTimeout(wheelSnapTimerRef.current);
      }

      wheelSnapTimerRef.current = window.setTimeout(() => {
        lockActiveCardDuringTransition();
        setCarouselIndex(index => Math.round(index));
        setIsDragging(false);
        resumeAutoRollingAfterDelay();
      }, WHEEL_SNAP_IDLE_MS);
    };

    stage.addEventListener('wheel', handleNativeWheel, { passive: false });

    return () => {
      stage.removeEventListener('wheel', handleNativeWheel);
      if (wheelSnapTimerRef.current) {
        window.clearTimeout(wheelSnapTimerRef.current);
      }
    };
  }, []);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointerStartXRef.current = event.clientX;
    pointerStartIndexRef.current = carouselIndex;
    pointerMovedRef.current = false;
    pauseAutoRolling();
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const startX = pointerStartXRef.current;
    if (startX === null) return;

    if (Math.abs(event.clientX - startX) < TAP_MOVE_THRESHOLD) return;

    if (!pointerMovedRef.current) {
      pointerMovedRef.current = true;
      clearTransitionLock();
      setIsDragging(true);
    }

    const deltaIndex = (startX - event.clientX) / cardSpacing;
    setCarouselIndex(pointerStartIndexRef.current + deltaIndex);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    finishPointerInteraction(event.clientX);
  };

  const activateCard = (virtualIndex: number) => {
    if (wheelSnapTimerRef.current) {
      window.clearTimeout(wheelSnapTimerRef.current);
    }

    setIsDragging(false);
    lockActiveCardDuringTransition();
    setCarouselIndex(virtualIndex);
    resumeAutoRollingAfterDelay();
  };

  const performCardAction = ({
    card,
    isActive,
    virtualIndex,
  }: {
    card: HomeCard;
    isActive: boolean;
    virtualIndex: number;
  }) => {
    if (isTransitionLocked && !isActive) return;

    if (!isActive) {
      activateCard(virtualIndex);
      return;
    }

    const href = getCardHref(card.category);
    if (!href) return;

    trackEvent('home', 'click_banner_category', {
      category_id: card.category.id,
      category_name: card.category.name,
    });

    if (href.startsWith('http')) {
      window.location.assign(href);
      return;
    }

    router.push(href);
  };

  const handleCardClick = ({
    card,
    isActive,
    virtualIndex,
  }: {
    card: HomeCard;
    isActive: boolean;
    virtualIndex: number;
  }) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }

    performCardAction({ card, isActive, virtualIndex });
  };

  const handleCardPointerUp = ({
    event,
    card,
    isActive,
    virtualIndex,
  }: {
    event: ReactPointerEvent<HTMLButtonElement>;
    card: HomeCard;
    isActive: boolean;
    virtualIndex: number;
  }) => {
    const startX = pointerStartXRef.current;

    if (
      startX === null ||
      pointerMovedRef.current ||
      Math.abs(event.clientX - startX) >= TAP_MOVE_THRESHOLD
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    pointerStartXRef.current = null;
    pointerMovedRef.current = false;
    setIsDragging(false);
    suppressNextClickRef.current = true;

    performCardAction({ card, isActive, virtualIndex });
  };

  return (
    <div {...qaAttr(qaId('home', 'page'))} className={styles.root}>
      <div className={styles.backgroundLayer}>
        {activeBackgroundUrl ? (
          <img
            className={styles.backgroundImage}
            src={activeBackgroundUrl}
            alt=""
            aria-hidden
          />
        ) : null}
      </div>
      <div className={styles.dimLayer} />

      <header className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.desktopLogo}>
            <VuddyLogoLink />
          </div>
          <div className={styles.mobileLogo}>
            <VuddyShortLogoLink />
          </div>
          <div className={styles.topBarTail}>
            <NavigationBarControls qaScope="home" />
          </div>
        </div>
      </header>

      {notice ? (
        <button
          type="button"
          className={styles.noticeArea}
          aria-label={`공지: ${notice.content}`}
          onClick={() => {
            if (!notice.landingUrl) return;
            window.location.assign(notice.landingUrl);
          }}>
          <span className={styles.noticePill}>
            <span className={styles.noticeTag}>공지</span>
            <span className={styles.noticeMarquee} aria-hidden="true">
              <span className={styles.noticeLoop}>
                {Array.from({ length: NOTICE_MARQUEE_GROUP_COUNT }).map(
                  (_, groupIndex) => (
                    <span key={groupIndex} className={styles.noticeGroup}>
                      {Array.from({ length: NOTICE_TEXT_REPEAT_COUNT }).map(
                        (_, textIndex) => (
                          <span key={textIndex} className={styles.noticeText}>
                            {notice.content}
                          </span>
                        )
                      )}
                    </span>
                  )
                )}
              </span>
            </span>
          </span>
        </button>
      ) : null}

      <section
        ref={cardStageRef}
        className={styles.cardStage}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={resetPointerInteraction}
        aria-label="카테고리 동영상 배너">
        {cards.length > 0 ? (
          <div className={styles.carouselPlane}>
            {renderedCards.map(renderedCard => {
              const { card, cardIndex, renderKey, virtualIndex, offset } =
                renderedCard;
              const absOffset = Math.abs(offset);
              const isVisible = absOffset <= visibleRadius;
              const isCentered = offset === 0;
              const isVisualActive = cardIndex === activeIndex;
              const scale = getCardScale(absOffset);
              const elevationProgress = clamp(1 - absOffset, 0, 1);
              const opacity = isVisible ? clamp(1 - absOffset * 0.38, 0, 1) : 0;
              const shouldRenderVideo = isVisible && !!card.videoUrl;
              const cardHref = getCardHref(card.category);
              const isCardActionable = !isCentered || !!cardHref;
              const canInteractWithCard = isVisible && isCardActionable;
              const cardTransform = `translate(-50%, -50%) translateX(${
                offset * cardSpacing
              }px) scale(${scale})`;

              return (
                <div
                  key={renderKey}
                  className={styles.cardFrame}
                  style={
                    {
                      opacity,
                      pointerEvents: 'none',
                      transform: cardTransform,
                      transition: isDragging ? 'none' : CARD_TRANSITION,
                      zIndex: isVisualActive ? 100 : 100 - absOffset,
                    } as CSSProperties
                  }>
                  <button
                    type="button"
                    className={styles.card}
                    style={
                      {
                        boxShadow: getCardElevationShadow(elevationProgress),
                        pointerEvents: canInteractWithCard ? 'auto' : 'none',
                      } as CSSProperties
                    }
                    disabled={isCentered && !cardHref}
                    data-active={isVisualActive}
                    data-actionable={isCardActionable}
                    data-home-card="true"
                    data-card-key={card.key}
                    data-render-key={renderKey}
                    data-offset={offset}
                    data-virtual-index={virtualIndex}
                    aria-label={
                      cardHref
                        ? `${card.category.name} 카테고리로 이동`
                        : `${card.category.name} 카드`
                    }
                    onPointerUpCapture={event =>
                      handleCardPointerUp({
                        event,
                        card,
                        isActive: isCentered,
                        virtualIndex,
                      })
                    }
                    onClick={() =>
                      handleCardClick({
                        card,
                        isActive: isCentered,
                        virtualIndex,
                      })
                    }>
                    {card.posterUrl ? (
                      <img
                        className={styles.cardPoster}
                        src={card.posterUrl}
                        alt=""
                        draggable={false}
                      />
                    ) : null}
                    {shouldRenderVideo ? (
                      <HomeHlsVideo
                        className={styles.cardVideo}
                        src={card.videoUrl!}
                        poster={card.posterUrl}
                        playing={isVisible}
                      />
                    ) : null}
                    {!isVisualActive ? (
                      <span className={styles.cardDim} />
                    ) : null}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            {isError
              ? '카테고리 영상을 불러오지 못했어요.'
              : '등록된 카테고리 영상이 없어요.'}
          </div>
        )}
      </section>

      {isLoggedIn && followingChannelsQuery.isSuccess ? (
        <MyCreatorDock
          channels={followingChannels}
          onAddClick={openChannelSearch}
          onInteractionEnd={resumeAutoRollingAfterDelay}
          onInteractionStart={pauseAutoRolling}
        />
      ) : null}
    </div>
  );
};

interface MyCreatorDockProps {
  channels: IHomeFollowingChannelResponse[];
  onAddClick: () => void;
  onInteractionEnd: () => void;
  onInteractionStart: () => void;
}

const MyCreatorDock = ({
  channels,
  onAddClick,
  onInteractionEnd,
  onInteractionStart,
}: MyCreatorDockProps) => {
  const routerPath = getRouterPath();
  const hasChannels = channels.length > 0;

  return (
    <div className={styles.creatorDockPosition}>
      <div
        className={clsx(
          styles.creatorDock,
          hasChannels && styles.hasChannelsDock
        )}>
        {hasChannels ? (
          <div
            className={styles.creatorListInteraction}
            onPointerCancelCapture={onInteractionEnd}
            onPointerDownCapture={onInteractionStart}
            onPointerUpCapture={onInteractionEnd}>
            <HorizontalScrollbar
              className={styles.creatorListViewport}
              slideClassName={styles.creatorListItem}
              slideWidth={40}>
              <button
                type="button"
                className={clsx(styles.creatorAddButton, styles.largeAddButton)}
                onClick={() => {
                  trackEvent('home', 'click_home_search', {});
                  onAddClick();
                }}
                aria-label="채널 검색">
                <Icon name="OUTLINE_PLUS" color="whitePrimary" size="24px" />
              </button>
              {channels.map(({ channel, hasRedDot }) => (
                <Link
                  key={channel.id}
                  className={styles.creatorLink}
                  href={routerPath.channelsHome({ channelUrl: channel.url })}
                  prefetch={false}
                  onClick={() =>
                    trackEvent('home', 'click_my_creator', {
                      channel_id: channel.id,
                      channel_name: channel.name,
                    })
                  }>
                  <ProfileImage
                    imageSize="base"
                    url={channel.thumbnail?.url ?? channel.coverImage?.url}
                    alt={channel.name}
                  />
                  {hasRedDot ? <span className={styles.creatorRedDot} /> : null}
                </Link>
              ))}
            </HorizontalScrollbar>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={clsx(styles.creatorAddButton, styles.smallAddButton)}
              onClick={() => {
                trackEvent('home', 'click_home_search', {});
                onAddClick();
              }}
              aria-label="채널 검색">
              <Icon name="OUTLINE_PLUS" color="whitePrimary" size="16px" />
            </button>
            <Element as="span" className={styles.creatorEmptyText}>
              채널을 팔로우 해보세요!
            </Element>
          </>
        )}
      </div>
    </div>
  );
};
